/**
 * ==========================================================================
 * SCAPE GEAR - GLOBAL JAVASCRIPT
 * Description: Single JavaScript file for the entire Scape Gear theme.
 * Structured by section to ensure clean, reusable, and modular code.
 * ==========================================================================
 */

document.addEventListener('DOMContentLoaded', () => {

    // --------------------------------------------------------------------------
    // RUN INITIALIZERS
    // --------------------------------------------------------------------------

    // Mosaic Banner Slider
    if (!customElements.get('mosaic-slider')) {
        class MosaicSlider extends HTMLElement {
            constructor() {
                super();
            }

            connectedCallback() {
                this.track = this.querySelector('.scape-mosaic__track');
                this.dots = this.querySelectorAll('.scape-mosaic__dot');
                this.items = this.querySelectorAll('.scape-mosaic__item');

                if (!this.track || !this.items.length) return;

                // Configuration from data attributes
                this.autoplay = this.dataset.autoplay === 'true';
                this.loop = this.dataset.loop === 'true';
                this.speed = parseInt(this.dataset.speed) || 3;
                this.autoplayInterval = null;

                this.init();
            }

            init() {
                // Initial dot update
                this.updateActiveDot();

                // Update dots on scroll
                this.track.addEventListener('scroll', () => {
                    this.updateActiveDot();
                }, { passive: true });

                // Click dot to scroll
                this.dots.forEach((dot, index) => {
                    dot.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.stopAutoplay();
                        this.scrollToIndex(index);
                        if (this.autoplay) {
                            // Restart autoplay after interaction delay
                            setTimeout(() => this.startAutoplay(), 2000);
                        }
                    });
                });

                if (this.autoplay && this.items.length > 1) {
                    this.startAutoplay();

                    // Pause on interaction
                    this.addEventListener('mouseenter', () => this.stopAutoplay());
                    this.addEventListener('mouseleave', () => this.startAutoplay());
                    this.track.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
                }
            }

            updateActiveDot() {
                if (!this.dots.length) return;

                const trackRect = this.track.getBoundingClientRect();
                const trackCenter = trackRect.left + trackRect.width / 2;

                let activeIndex = 0;
                let minDistance = Infinity;

                this.items.forEach((item, index) => {
                    const itemRect = item.getBoundingClientRect();
                    const itemCenter = itemRect.left + itemRect.width / 2;
                    const distance = Math.abs(trackCenter - itemCenter);

                    if (distance < minDistance) {
                        minDistance = distance;
                        activeIndex = index;
                    }
                });

                this.items.forEach((item, index) => {
                    const isActive = index === activeIndex;
                    item.classList.toggle('is-active', isActive);
                    if (this.dots[index]) {
                        this.dots[index].classList.toggle('is-active', isActive);
                    }
                });
            }

            scrollToIndex(index) {
                const item = this.items[index];
                if (!item) return;

                // Use scrollIntoView if behavior: smooth is handled by CSS, 
                // or calculate manual scrollLeft for cross-browser consistency
                const trackRect = this.track.getBoundingClientRect();
                const itemRect = item.getBoundingClientRect();
                const scrollOffset = itemRect.left - trackRect.left + this.track.scrollLeft;

                this.track.scrollTo({
                    left: scrollOffset,
                    behavior: 'smooth'
                });
            }

            startAutoplay() {
                if (this.autoplayInterval || !this.autoplay) return;
                this.autoplayInterval = setInterval(() => {
                    let currentIndex = Array.from(this.dots).findIndex(dot => dot.classList.contains('is-active'));
                    if (currentIndex === -1) currentIndex = 0;

                    let nextIndex = currentIndex + 1;
                    if (nextIndex >= this.items.length) {
                        if (this.loop) {
                            nextIndex = 0;
                        } else {
                            this.stopAutoplay();
                            return;
                        }
                    }

                    this.scrollToIndex(nextIndex);
                }, this.speed * 1000);
            }

            stopAutoplay() {
                if (this.autoplayInterval) {
                    clearInterval(this.autoplayInterval);
                    this.autoplayInterval = null;
                }
            }
        }
        customElements.define('mosaic-slider', MosaicSlider);
    }

    // --------------------------------------------------------------------------
    // PRODUCT TABS & QUICK ADD
    // --------------------------------------------------------------------------

    // Tab Switcher
    const tabButtons = document.querySelectorAll('.scape-tabs__btn');
    tabButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const parent = btn.closest('.scape-tabs');
            const tabId = btn.dataset.tab;

            // Update buttons
            parent.querySelectorAll('.scape-tabs__btn').forEach(b => b.classList.remove('is-active'));
            btn.classList.add('is-active');

            // Update panes
            parent.querySelectorAll('.scape-tabs__pane').forEach(p => p.classList.remove('is-active'));
            const activePane = parent.querySelector(`#${tabId}`);
            activePane.classList.add('is-active');
            
            // Force restart any sliders in the newly active tab
            activePane.querySelectorAll('mobile-autoplay-slider').forEach(slider => {
                if (typeof slider.checkMobile === 'function') {
                    slider.checkMobile();
                }
            });
        });
    });

    // Mobile Footer Accordions
    const footerAccordions = document.querySelectorAll('.scape-footer__col--accordion .scape-footer__col-title');
    footerAccordions.forEach(title => {
        title.addEventListener('click', () => {
            if (window.innerWidth <= 600) {
                const parent = title.closest('.scape-footer__col--accordion');
                parent.classList.toggle('is-open');
            }
        });
    });

    // AJAX Quick Add to Cart
    const quickAddButtons = document.querySelectorAll('.scape-product-card__quick-add');
    quickAddButtons.forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.preventDefault();
            const variantId = btn.dataset.variantId;
            if (!variantId) return;

            btn.classList.add('is-loading');
            btn.disabled = true;

            try {
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        items: [{
                            id: variantId,
                            quantity: 1
                        }]
                    })
                });

                if (response.ok) {
                    // Trigger cart refresh if theme supports it
                    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                        bubbles: true
                    }));
                    
                    // Open the cart drawer
                    const cartDrawerTrigger = document.querySelector('[aria-controls="cart-drawer"]');
                    if (cartDrawerTrigger) cartDrawerTrigger.click();

                    // Visual feedback
                    const originalIcon = btn.innerHTML;
                    btn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>';
                    setTimeout(() => {
                        btn.innerHTML = originalIcon;
                        btn.classList.remove('is-loading');
                        btn.disabled = false;
                    }, 2000);
                }
            } catch (error) {
                console.error('Quick add failed:', error);
                btn.classList.remove('is-loading');
                btn.disabled = false;
            }
        });
    });

    // --------------------------------------------------------------------------
    // BUILD YOUR OWN BUNDLE
    // --------------------------------------------------------------------------

    class BundleMarquee {
        constructor(wrap) {
            this.wrap = wrap;
            this.track = wrap.querySelector('.scape-bundle__marquee-track');
            if (!this.track) return;

            this.speed = 1; // Pixels per frame
            this.position = 0;
            this.paused = false;
            this.isStatic = false;

            // Store instance on the element so BundleBuilder can call restart()
            wrap._bundleMarqueeInstance = this;

            this.init();
        }

        init() {
            const itemsHTML = this.track.innerHTML;
            this.setupTrack(itemsHTML);

            this.wrap.addEventListener('mouseenter', () => this.paused = true);
            this.wrap.addEventListener('mouseleave', () => this.paused = false);

            this.animate();
        }

        setupTrack(itemsHTML) {
            // Temporary element to count products
            const temp = document.createElement('div');
            temp.innerHTML = itemsHTML;
            const productCount = temp.querySelectorAll('.scape-bundle__product').length;

            if (productCount <= 4) {
                // Keep single, center it, no scroll
                this.track.innerHTML = itemsHTML;
                this.isStatic = true;
                this.wrap.classList.add('is-static');
                this.track.style.transform = 'none';
            } else {
                // Loop infinitely, triple content
                this.track.innerHTML = itemsHTML + itemsHTML + itemsHTML;
                this.isStatic = false;
                this.wrap.classList.remove('is-static');
            }
        }

        restart(newTrack) {
            // Called after zone swap
            this.track = newTrack;
            this.position = 0;
            const itemsHTML = this.track.innerHTML;
            this.setupTrack(itemsHTML);
        }

        animate() {
            if (!this.paused && !this.isStatic) {
                this.position -= this.speed;

                // Reset position when 1/3 of the track has passed (since we tripled it)
                const trackWidth = this.track.scrollWidth / 3;
                if (Math.abs(this.position) >= trackWidth) {
                    this.position = 0;
                }

                this.track.style.transform = `translateX(${this.position}px)`;
            } else if (this.isStatic) {
                this.track.style.transform = 'none';
            }
            requestAnimationFrame(() => this.animate());
        }
    }

    class BundleBuilder {
        constructor(section) {
            this.section = section;
            this.section.bundleBuilderInstance = this;
            this.slotsContainer = section.querySelector('[data-bundle-slots]');
            this.totalDisplay = section.querySelector('[data-bundle-total]');
            this.tiers = section.querySelectorAll('.scape-bundle__tier');

            this.selectedProducts = [];
            this.maxSlots = 5;

            this.init();
        }

        init() {
            // Load the per-zone product data embedded in the page
            const sectionId = this.section.id.replace('scape-bundle-', '');
            const zoneDataEl = document.getElementById(`bundle-zone-data-${sectionId}`);
            this.zoneData = {};
            if (zoneDataEl) {
                try { this.zoneData = JSON.parse(zoneDataEl.textContent); } catch (e) { }
            }

            this.section.addEventListener('click', (e) => {
                const hotspot = e.target.closest('.scape-hotspot');
                if (hotspot) {
                    this.section.querySelectorAll('.scape-hotspot').forEach(h => h.classList.remove('is-active'));
                    hotspot.classList.add('is-active');

                    // Swap marquee to show this zone's collection products
                    const zoneKey = hotspot.dataset.zoneKey;
                    const zoneName = hotspot.dataset.zone;
                    this.swapMarquee(zoneKey, zoneName);

                    // Scroll slightly down to show the products marquee
                    const marqueeWrap = this.section.querySelector('[data-bundle-marquee]');
                    if (marqueeWrap) {
                        setTimeout(() => {
                            const headerOffset = 80;
                            const elementPosition = marqueeWrap.getBoundingClientRect().top;
                            const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
                            const offsetPosition = elementPosition + scrollY - headerOffset;
                            
                            // Robust iOS Safari smooth scroll
                            const originalBehavior = document.documentElement.style.scrollBehavior;
                            document.documentElement.style.scrollBehavior = 'smooth';
                            window.scrollTo(0, offsetPosition);
                            setTimeout(() => {
                                document.documentElement.style.scrollBehavior = originalBehavior;
                            }, 1000);
                        }, 300);
                    }
                }

                const removeBtn = e.target.closest('.scape-bundle__remove-slot');
                if (removeBtn) {
                    e.preventDefault();
                    const index = parseInt(removeBtn.dataset.index);
                    this.selectedProducts.splice(index, 1);
                    this.updateUI();
                }

                const checkoutBtn = e.target.closest('.scape-bundle__checkout');
                if (checkoutBtn) {
                    e.preventDefault();
                    if (this.selectedProducts.length === 0) return;

                    checkoutBtn.classList.add('is-loading');
                    const items = this.selectedProducts.map(p => ({ id: p.id, quantity: 1 }));

                    fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ items })
                    }).then(response => {
                        if (response.ok) {
                            // ScapeGear 30 Sep 2026: the bundle savings (2 items 5%, 3 items 10%, 4+ items 15%) are now discount codes that only this builder applies, not automatic discounts on every cart.
                            const sgCode = items.length >= 4 ? 'BUNDLE15' : (items.length === 3 ? 'BUNDLE10' : (items.length === 2 ? 'BUNDLE5' : ''));
                            return (sgCode ? fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: sgCode }) }).catch(() => {}) : Promise.resolve()).then(() => {
                            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                                bubbles: true
                            }));
                            
                            // Open the cart drawer
                            const cartDrawerTrigger = document.querySelector('[aria-controls="cart-drawer"]');
                            if (cartDrawerTrigger) cartDrawerTrigger.click();
                            
                            checkoutBtn.classList.remove('is-loading');
                            });
                        }
                    }).catch(error => {
                        console.error('Bundle add failed:', error);
                        checkoutBtn.classList.remove('is-loading');
                    });
                }
            });
        }

        swapMarquee(zoneKey, zoneName) {
            const marqueeWrap = this.section.querySelector('[data-bundle-marquee]');
            if (!marqueeWrap) return;

            // Make the marquee container pop up/slide down into view
            marqueeWrap.classList.add('is-visible');

            const track = marqueeWrap.querySelector('.scape-bundle__marquee-track');
            const zoneLabel = marqueeWrap.querySelector('[data-bundle-zone-label] span');
            const products = this.zoneData[zoneKey];

            // Update zone label
            if (zoneLabel) zoneLabel.textContent = zoneName ? `${zoneName} PRODUCTS` : 'ALL PRODUCTS';

            // Fade out → rebuild → fade in
            track.style.opacity = '0';
            track.style.transition = 'opacity 0.25s ease';

            setTimeout(() => {
                if (products && products.length > 0) {
                    track.innerHTML = products.map(p => `
            <div class="scape-bundle__product" data-product-id="${p.id}">
              <div class="scape-bundle__product-img">
                ${p.img
                            ? `<img src="${p.img}" alt="${p.title}" loading="lazy">`
                            : '<div class="scape-bundle__product-placeholder"></div>'
                        }
                <button class="scape-bundle__add"
                  data-id="${p.variant_id}"
                  data-img="${p.thumb}"
                  data-price="${p.price}"
                  aria-label="Add to bundle"
                >
                  <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </button>
              </div>
              <h4 class="scape-bundle__product-title">${p.title}</h4>
            </div>
          `).join('');
                }

                // Restart the BundleMarquee animation on this wrap
                const existingMarquee = marqueeWrap._bundleMarqueeInstance;
                if (existingMarquee) {
                    existingMarquee.restart(track);
                }

                track.style.opacity = '1';
            }, 260);
        }

        addProduct(addBtn) {
            const product = {
                id: addBtn.dataset.id,
                img: addBtn.dataset.img,
                price: parseInt(addBtn.dataset.price)
            };

            if (this.selectedProducts.length < this.maxSlots) {
                this.selectedProducts.push(product);
                this.updateUI();

                addBtn.style.transform = 'scale(1.2)';
                const originalIcon = addBtn.innerHTML;
                addBtn.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>';
                setTimeout(() => {
                    addBtn.style.transform = '';
                    addBtn.innerHTML = originalIcon;
                }, 600);
            }
        }
        updateUI() {
            const slots = this.slotsContainer.querySelectorAll('.scape-bundle__slot');
            slots.forEach((slot, index) => {
                const product = this.selectedProducts[index];
                if (product) {
                    slot.innerHTML = `
                        <img src="${product.img}" alt="Selected" loading="lazy">
                        <button class="scape-bundle__remove-slot" data-index="${index}" aria-label="Remove product">
                            <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="3" fill="none">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    `;
                    slot.style.borderColor = 'var(--scape-color-accent)';
                } else {
                    slot.innerHTML = '<div class="scape-bundle__slot-placeholder"></div>';
                    slot.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }
            });

            let rawTotal = this.selectedProducts.reduce((sum, p) => sum + p.price, 0);
            let count = this.selectedProducts.length;
            let discount = 0;
            if (count === 2) discount = 5;
            else if (count === 3) discount = 10;
            else if (count >= 4) discount = 15;

            let finalTotal = Math.round(rawTotal * (1 - (discount / 100)));
            this.totalDisplay.textContent = `R${finalTotal / 100}`;

            this.tiers.forEach(tier => {
                const tierNum = parseInt(tier.dataset.tier);
                tier.classList.toggle('is-active', tierNum <= count);
            });
        }
    }

    // Initialize
    document.querySelectorAll('.scape-bundle').forEach(section => {
        new BundleBuilder(section);
    });

    document.querySelectorAll('[data-bundle-marquee]').forEach(marquee => {
        new BundleMarquee(marquee);
    });

    // Step Marquee (Infinite & Non-stop)
    if (!customElements.get('step-marquee')) {
        class StepMarquee extends HTMLElement {
            constructor() {
                super();
                this.track = this.querySelector('.scape-step-slider__track');
                this.isPaused = false;
                this.isMobileSlider = false;
                this.animationStarted = false;
                this.currentMobileIndex = 0;
                this.mobileAutoplayInterval = null;
                this.userInteractionTimeout = null;

                // Desktop interaction handlers (pause on hover/touch)
                this.addEventListener('mouseenter', () => { if (!this.isMobileSlider) this.isPaused = true; });
                this.addEventListener('mouseleave', () => { if (!this.isMobileSlider) this.isPaused = false; });

                // Touch interaction pause/resume for autoplay on mobile
                this.addEventListener('touchstart', () => {
                    if (!this.isMobileSlider) {
                        this.isPaused = true;
                    } else {
                        this.stopMobileAutoplay();
                    }
                }, { passive: true });

                this.addEventListener('touchend', () => {
                    if (!this.isMobileSlider) {
                        this.isPaused = false;
                    } else {
                        clearTimeout(this.userInteractionTimeout);
                        this.userInteractionTimeout = setTimeout(() => this.startMobileAutoplay(), 5000);
                    }
                }, { passive: true });
            }

            connectedCallback() {
                if (!this.track) return;

                // Read settings from backend
                this.speed = parseFloat(this.dataset.speed) || 0.5;
                this.direction = this.dataset.direction || 'left';
                this.position = 0;
                this.originalHTML = this.track.innerHTML;

                this.init();

                this.resizeHandler = () => this.handleResize();
                window.addEventListener('resize', this.resizeHandler);
            }

            disconnectedCallback() {
                if (this.resizeHandler) {
                    window.removeEventListener('resize', this.resizeHandler);
                }
                if (this.scrollHandler) {
                    this.removeEventListener('scroll', this.scrollHandler);
                }
                this.stopMobileAutoplay();
                clearTimeout(this.userInteractionTimeout);

                // Clear dots content instead of removing the block
                const parentSlider = this.closest('.scape-step-slider') || this.parentElement;
                if (parentSlider) {
                    const paginationEl = parentSlider.querySelector('.scape-step-slider__dots');
                    if (paginationEl) paginationEl.innerHTML = '';
                }
            }

            init() {
                const isMobile = window.innerWidth <= 991 || this.closest('.scape-step-detail') !== null;
                if (isMobile) {
                    this.isMobileSlider = true;
                    this.classList.add('is-mobile-slider');

                    // Revert to clean HTML
                    this.track.innerHTML = this.originalHTML;
                    this.track.style.transform = 'none';

                    // Populates the static dots block located OUTSIDE step-marquee in liquid
                    const parentSlider = this.closest('.scape-step-slider') || this.parentElement;
                    const items = this.querySelectorAll('.scape-step-slider__item');
                    let paginationEl = parentSlider.querySelector('.scape-step-slider__dots');
                    if (paginationEl) {
                        paginationEl.innerHTML = '';

                        items.forEach((item, index) => {
                            const dot = document.createElement('button');
                            dot.className = `scape-step-slider__dot${index === 0 ? ' is-active' : ''}`;
                            dot.setAttribute('aria-label', `Go to slide ${index + 1}`);

                            // Dot click event to center the slide and delay autoplay
                            dot.addEventListener('click', () => {
                                this.scrollToSlide(index);
                                this.stopMobileAutoplay();
                                clearTimeout(this.userInteractionTimeout);
                                this.userInteractionTimeout = setTimeout(() => this.startMobileAutoplay(), 5000);
                            });

                            paginationEl.appendChild(dot);
                        });
                    }

                    // Scroll listener to update dots in real-time on manual swipe
                    this.scrollHandler = () => this.updateActiveDot();
                    this.addEventListener('scroll', this.scrollHandler, { passive: true });

                    // Start Autoplay
                    this.currentMobileIndex = 0;
                    this.startMobileAutoplay();
                    
                    // Initialize dynamic height immediately
                    setTimeout(() => {
                        this.updateActiveDot();
                    }, 50);

                } else {
                    // Desktop mode
                    this.isMobileSlider = false;
                    this.classList.remove('is-mobile-slider');

                    // Clean up mobile autoplay & listeners
                    this.stopMobileAutoplay();
                    clearTimeout(this.userInteractionTimeout);
                    if (this.scrollHandler) {
                        this.removeEventListener('scroll', this.scrollHandler);
                    }

                    // Clear static dots container content in desktop mode
                    const parentSlider = this.closest('.scape-step-slider') || this.parentElement;
                    const paginationEl = parentSlider.querySelector('.scape-step-slider__dots');
                    if (paginationEl) paginationEl.innerHTML = '';

                    this.track.style.transform = 'none';

                    // Clone items for infinite effect (Triple them)
                    this.track.innerHTML = this.originalHTML + this.originalHTML + this.originalHTML;

                    // Wait a bit for layout to settle
                    setTimeout(() => {
                        this.calculateStart();
                        if (!this.animationStarted) {
                            this.animationStarted = true;
                            this.animate();
                        }
                    }, 100);
                }
            }

            handleResize() {
                const isMobile = window.innerWidth <= 991 || this.closest('.scape-step-detail') !== null;
                if (isMobile && !this.isMobileSlider) {
                    this.init();
                } else if (!isMobile && this.isMobileSlider) {
                    this.init();
                }
            }

            calculateStart() {
                if (this.isMobileSlider) return;
                const trackWidth = this.track.scrollWidth / 3;
                if (this.direction === 'right') {
                    this.position = -trackWidth;
                } else {
                    this.position = 0;
                }
            }

            animate() {
                if (this.isMobileSlider) {
                    this.track.style.transform = 'none';
                    this.animationStarted = false; // Stop loop and mark as stopped
                    return;
                }
                if (!this.isPaused) {
                    if (this.direction === 'right') {
                        this.position += this.speed;
                    } else {
                        this.position -= this.speed;
                    }
                    this.updatePosition();
                }
                requestAnimationFrame(() => this.animate());
            }

            updatePosition() {
                if (this.isMobileSlider) return;
                const trackWidth = this.track.scrollWidth / 3;
                if (trackWidth === 0) return;

                // Loop logic for seamless reset
                if (this.position <= -trackWidth * 2) {
                    this.position += trackWidth;
                } else if (this.position >= 0) {
                    this.position -= trackWidth;
                }

                this.track.style.transform = `translateX(${this.position}px)`;
            }

            // ----------------------------------------------------
            // Mobile Slider Helpers (Vanilla Swipe & Autoplay)
            // ----------------------------------------------------
            updateActiveDot() {
                if (!this.isMobileSlider) return;
                const isVertical = window.innerWidth <= 0; // Temporarily 0, was 520
                const scrollLeft = this.scrollLeft;
                const width = this.offsetWidth;
                const trackItems = this.querySelectorAll('.scape-step-slider__item');
                if (trackItems.length === 0) return;

                let activeIndex = 0;
                let minDistance = Infinity;

                trackItems.forEach((item, index) => {
                    const itemRect = item.getBoundingClientRect();
                    const containerRect = this.getBoundingClientRect();
                    
                    let distance;
                    if (isVertical) {
                        const itemOffset = itemRect.top - containerRect.top + this.scrollTop;
                        distance = Math.abs(itemOffset - this.scrollTop);
                    } else {
                        const itemOffset = itemRect.left - containerRect.left + scrollLeft;
                        distance = Math.abs(itemOffset - scrollLeft - (width - itemRect.width) / 2);
                    }

                    if (distance < minDistance) {
                        minDistance = distance;
                        activeIndex = index;
                    }
                });

                const parentSlider = this.closest('.scape-step-slider') || this.parentElement;
                
                // Update Progress Bar
                const progressBar = parentSlider.querySelector('.scape-step-progress__bar');
                if (progressBar) {
                    const scrollWidth = this.scrollWidth;
                    const clientWidth = this.clientWidth;
                    let progress = 0;
                    if (scrollWidth > clientWidth) {
                        progress = ((scrollLeft + clientWidth) / scrollWidth) * 100;
                    } else {
                        progress = 100;
                    }
                    progress = Math.max(0, Math.min(100, progress));
                    progressBar.style.width = `${progress}%`;
                }

                this.currentMobileIndex = activeIndex;

                // Dynamically adjust height to current item in vertical mode
                if (isVertical) {
                    const activeItem = trackItems[activeIndex];
                    if (activeItem) {
                        this.style.height = activeItem.offsetHeight + 'px';
                    }
                } else {
                    this.style.height = 'auto'; // Reset if horizontal
                }
            }

            scrollToSlide(index) {
                if (!this.isMobileSlider) return;
                const isVertical = window.innerWidth <= 0; // Temporarily 0, was 520
                const trackItems = this.querySelectorAll('.scape-step-slider__item');
                const item = trackItems[index];
                if (!item) return;

                const itemRect = item.getBoundingClientRect();
                const containerRect = this.getBoundingClientRect();

                if (isVertical) {
                    const itemOffset = itemRect.top - containerRect.top + this.scrollTop;
                    this.scrollTo({
                        top: itemOffset,
                        behavior: 'smooth'
                    });
                } else {
                    const itemOffset = itemRect.left - containerRect.left + this.scrollLeft;
                    const scrollOffset = itemOffset - (containerRect.width - itemRect.width) / 2;
                    this.scrollTo({
                        left: scrollOffset,
                        behavior: 'smooth'
                    });
                }
            }

            startMobileAutoplay() {
                this.stopMobileAutoplay();
                // Autoplay disabled per request for progress bar slider
                /*
                this.mobileAutoplayInterval = setInterval(() => {
                    const trackItems = this.querySelectorAll('.scape-step-slider__item');
                    if (trackItems.length === 0) return;

                    this.currentMobileIndex = (this.currentMobileIndex + 1) % trackItems.length;
                    this.scrollToSlide(this.currentMobileIndex);
                }, 4000);
                */
            }

            stopMobileAutoplay() {
                if (this.mobileAutoplayInterval) {
                    clearInterval(this.mobileAutoplayInterval);
                    this.mobileAutoplayInterval = null;
                }
            }
        }
        customElements.define('step-marquee', StepMarquee);
    }

    // Global delegation for Add to Bundle buttons (immune to innerHTML clones)
    document.addEventListener('click', (e) => {
        const addBtn = e.target.closest('.scape-bundle__add');
        if (addBtn) {
            e.preventDefault();
            const section = addBtn.closest('.scape-bundle');
            if (section && section.bundleBuilderInstance) {
                section.bundleBuilderInstance.addProduct(addBtn);
            }
        }
    });

    /**
     * Scape Verdict Swiper Initialization
     * Uses Swiper.js for high-fidelity multi-item slider performance.
     */
    const initVerdictSliders = () => {
        const swiperContainers = document.querySelectorAll('.scape-verdict__swiper');

        swiperContainers.forEach(container => {
            // Avoid re-initialization
            if (container.classList.contains('swiper-initialized')) return;

            const autoplayEnabled = container.dataset.autoplay === 'true';

            new Swiper(container, {
                slidesPerView: 1.2,
                spaceBetween: 20,
                loop: true,
                grabCursor: true,
                autoplay: autoplayEnabled ? {
                    delay: 5000,
                    disableOnInteraction: false,
                } : false,
                pagination: {
                    el: container.querySelector('.scape-verdict__pagination'),
                    clickable: true,
                },
                breakpoints: {
                    640: {
                        slidesPerView: 2,
                        spaceBetween: 20,
                    },
                    1024: {
                        slidesPerView: 3,
                        spaceBetween: 30,
                    },
                    1280: {
                        slidesPerView: 4,
                        spaceBetween: 30,
                    },
                    1500: {
                        slidesPerView: 5,
                        spaceBetween: 30,
                    },
                },
            });
        });
    };
    /**
     * Scape Custom Slider (High-Fidelity)
     * Custom built for the Flip Slider to ensure perfect centering, loop, and scaling.
     */
    if (!customElements.get('scape-custom-slider')) {
        class ScapeCustomSlider extends HTMLElement {
            constructor() {
                super();
                this.currentIndex = 0;
                this.isMoving = false;
                this.autoplayInterval = null;
            }

            connectedCallback() {
                this.track = this.querySelector('.scape-flip-slider__track');
                this.slides = Array.from(this.track.querySelectorAll('.scape-flip-slider__slide'));
                this.prevBtn = this.querySelector('.scape-flip-slider__arrow--prev');
                this.nextBtn = this.querySelector('.scape-flip-slider__arrow--next');

                if (!this.track || !this.slides.length) return;

                this.autoplay = this.dataset.autoplay === 'true';
                this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5000;
                this.loop = this.dataset.loop === 'true';

                this.init();
            }

            init() {
                // Clone slides for seamless loop (Clone the whole set twice)
                const slideCount = this.slides.length;
                const firstSet = this.slides.map(s => s.cloneNode(true));
                const lastSet = this.slides.map(s => s.cloneNode(true));

                firstSet.forEach(s => this.track.appendChild(s));
                lastSet.forEach(s => this.track.insertBefore(s, this.track.firstChild));

                // Update slides list after cloning
                this.allSlides = Array.from(this.track.querySelectorAll('.scape-flip-slider__slide'));
                this.currentIndex = slideCount; // Start at the beginning of the original set

                // Initial Position
                this.updatePosition(false);

                // Events
                this.prevBtn?.addEventListener('click', () => this.prev());
                this.nextBtn?.addEventListener('click', () => this.next());

                if (this.autoplay) this.startAutoplay();

                // Pause on hover
                this.addEventListener('mouseenter', () => this.stopAutoplay());
                this.addEventListener('mouseleave', () => this.startAutoplay());
            }

            updatePosition(animate = true) {
                if (animate) {
                    this.track.style.transition = 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';
                } else {
                    this.track.style.transition = 'none';
                }

                const viewportWidth = this.querySelector('.scape-flip-slider__viewport').offsetWidth;
                const slideWidth = this.allSlides[0].offsetWidth;
                const slideMargin = parseInt(window.getComputedStyle(this.allSlides[0]).marginLeft) +
                    parseInt(window.getComputedStyle(this.allSlides[0]).marginRight);
                const fullSlideWidth = slideWidth + slideMargin;

                // Calculate offset to center the slide
                const offset = (viewportWidth / 2) - (slideWidth / 2) - (this.currentIndex * fullSlideWidth);

                this.track.style.transform = `translateX(${offset}px)`;

                // Update active classes
                this.allSlides.forEach((slide, idx) => {
                    slide.classList.toggle('is-active', idx === this.currentIndex);
                });

                // Loop check
                if (animate) {
                    this.track.addEventListener('transitionend', () => this.checkLoop(), { once: true });
                }
            }

            checkLoop() {
                const slideCount = this.slides.length;
                if (this.currentIndex <= slideCount - 1) {
                    this.currentIndex += slideCount;
                    this.updatePosition(false);
                } else if (this.currentIndex >= slideCount * 2) {
                    this.currentIndex -= slideCount;
                    this.updatePosition(false);
                }
            }

            next() {
                if (this.isMoving) return;
                this.currentIndex++;
                this.updatePosition();
            }

            prev() {
                if (this.isMoving) return;
                this.currentIndex--;
                this.updatePosition();
            }

            startAutoplay() {
                if (!this.autoplay) return;
                this.stopAutoplay();
                this.autoplayInterval = setInterval(() => this.next(), this.autoplaySpeed);
            }

            stopAutoplay() {
                if (this.autoplayInterval) {
                    clearInterval(this.autoplayInterval);
                    this.autoplayInterval = null;
                }
            }
        }
        customElements.define('scape-custom-slider', ScapeCustomSlider);
    }

    // Initial load
    initVerdictSliders();


    // --------------------------------------------------------------------------
    // EXPECT STATS COUNTER
    // --------------------------------------------------------------------------

    const initExpectStats = () => {
        const section = document.querySelector('.scape-expect');
        if (!section) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    animateStats(section);
                    observer.unobserve(section);
                }
            });
        }, { threshold: 0.2 });

        observer.observe(section);
    };

    const animateStats = (section) => {
        // 1. Animate Bars
        const bars = section.querySelectorAll('.scape-expect__bar-fill');
        bars.forEach(bar => {
            const target = bar.dataset.percentage;
            bar.style.width = `${target}%`;
        });

        // 2. Animate Numbers
        const numbers = section.querySelectorAll('.scape-expect__stat-number');
        numbers.forEach(numEl => {
            const targetStr = numEl.dataset.count;
            const targetNum = parseFloat(targetStr.replace(/[^0-9.]/g, ''));
            if (isNaN(targetNum)) return;

            const suffix = targetStr.replace(/[0-9.,]/g, '');
            const hasComma = targetStr.includes(',');
            const decimalPlaces = (targetStr.split('.')[1] || '').length;

            let startTimestamp = null;
            const duration = 2000;

            const step = (timestamp) => {
                if (!startTimestamp) startTimestamp = timestamp;
                const progress = Math.min((timestamp - startTimestamp) / duration, 1);

                // Easing function: easeOutExpo
                const easedProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
                const currentNum = easedProgress * targetNum;

                let formattedNum = currentNum.toFixed(decimalPlaces);
                if (hasComma) {
                    formattedNum = Math.floor(currentNum).toLocaleString();
                }

                numEl.textContent = formattedNum + suffix;

                if (progress < 1) {
                    window.requestAnimationFrame(step);
                } else {
                    numEl.textContent = targetStr; // Ensure final value is exact
                }
            };

            window.requestAnimationFrame(step);
        });
    };

    initExpectStats();

    // Shopify Theme Editor reload support
    document.addEventListener('shopify:section:load', (event) => {
        if (event.target.classList.contains('scape-verdict') || event.target.querySelector('.scape-verdict')) {
            initVerdictSliders();
        }

        // Initialize FAQ if reloaded
        if (event.target.classList.contains('scape-faq') || event.target.querySelector('.scape-faq')) {
            initFAQ();
        }

        // Initialize Expect Stats if reloaded
        if (event.target.classList.contains('scape-expect') || event.target.querySelector('.scape-expect')) {
            initExpectStats();
        }

        // Initialize Steps Animation if reloaded
        if (event.target.classList.contains('scape-steps') || event.target.querySelector('.scape-steps')) {
            initStepsAnimation();
        }
    });

    const initStepsAnimation = () => {
        const steps = document.querySelectorAll('.scape-steps__item');
        if (steps.length === 0) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    // No unobserve if we want it to repeat, but usually once is better
                    // observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.2 });

        steps.forEach(step => observer.observe(step));
    };

    initStepsAnimation();

    const initFAQ = () => {
        const faqItems = document.querySelectorAll('.scape-faq__item');
        faqItems.forEach(item => {
            const question = item.querySelector('.scape-faq__question');
            if (question.dataset.initialized) return;
            question.dataset.initialized = 'true';

            question.addEventListener('click', () => {
                const isActive = item.classList.contains('is-active');

                // Close all other items
                faqItems.forEach(otherItem => {
                    otherItem.classList.remove('is-active');
                    otherItem.querySelector('.scape-faq__question').setAttribute('aria-expanded', 'false');
                });

                // Toggle current item
                if (!isActive) {
                    item.classList.add('is-active');
                    question.setAttribute('aria-expanded', 'true');
                }
            });
        });
    };

    initFAQ();

});

// Feature Marquee (Isolated High-Impact Section)
if (!customElements.get('feature-marquee')) {
    class FeatureMarquee extends HTMLElement {
        constructor() {
            super();
            this.track = this.querySelector('.scape-feature-marquee__track');
        }

        connectedCallback() {
            if (!this.track) return;

            this.speed = parseFloat(this.dataset.speed) || 0.8;
            this.position = 0;
            this.isPaused = false;

            this.init();
        }

        init() {
            const items = this.track.innerHTML;
            this.track.innerHTML = items + items + items;

            this.addEventListener('mouseenter', () => this.isPaused = true);
            this.addEventListener('mouseleave', () => this.isPaused = false);
            this.addEventListener('touchstart', () => this.isPaused = true, { passive: true });
            this.addEventListener('touchend', () => this.isPaused = false, { passive: true });

            this.animate();
        }

        animate() {
            if (!this.isPaused) {
                this.position -= this.speed;
                this.updatePosition();
            }
            requestAnimationFrame(() => this.animate());
        }

        updatePosition() {
            const trackWidth = this.track.scrollWidth / 3;
            if (trackWidth === 0) return;

            if (this.position <= -trackWidth) {
                this.position += trackWidth;
            }
            this.track.style.transform = `translateX(${this.position}px)`;
        }
    }
    customElements.define('feature-marquee', FeatureMarquee);
}

// Logo Marquee (Seamless Brand Slider)
if (!customElements.get('logo-marquee')) {
    class LogoMarquee extends HTMLElement {
        constructor() {
            super();
            this.track = this.querySelector('.scape-logo-marquee__track');
        }

        connectedCallback() {
            if (!this.track) return;

            this.speed = parseFloat(this.dataset.speed) || 1;
            this.position = 0;
            this.isPaused = false;

            this.init();
        }

        init() {
            // Clone items for seamless loop
            const items = this.track.innerHTML;
            this.track.innerHTML = items + items + items;

            this.addEventListener('mouseenter', () => this.isPaused = true);
            this.addEventListener('mouseleave', () => this.isPaused = false);
            this.addEventListener('touchstart', () => this.isPaused = true, { passive: true });
            this.addEventListener('touchend', () => this.isPaused = false, { passive: true });

            this.animate();
        }

        animate() {
            if (!this.isPaused) {
                this.position -= this.speed;

                const trackWidth = this.track.scrollWidth / 3;
                if (Math.abs(this.position) >= trackWidth) {
                    this.position = 0;
                }
                this.track.style.transform = `translateX(${this.position}px)`;
            }
            requestAnimationFrame(() => this.animate());
        }
    }
    customElements.define('logo-marquee', LogoMarquee);
}

// Scape Video Marquee
if (!customElements.get('scape-video-marquee')) {
    class ScapeVideoMarquee extends HTMLElement {
        constructor() {
            super();
            this.track = this.querySelector('.scape-video-marquee__track');
        }

        connectedCallback() {
            if (!this.track) return;

            // Optional speed setting (default to 0.5)
            this.speed = parseFloat(this.dataset.speed) || 0.5;
            this.position = 0;
            this.isPaused = false;

            this.init();
        }

        init() {
            // Clone items for seamless loop
            const items = this.track.innerHTML;
            this.track.innerHTML = items + items + items;

            this.addEventListener('mouseenter', () => this.isPaused = true);
            this.addEventListener('mouseleave', () => this.isPaused = false);
            this.addEventListener('touchstart', () => this.isPaused = true, { passive: true });
            this.addEventListener('touchend', () => this.isPaused = false, { passive: true });

            // Handle video play/pause on hover or click
            const videos = this.querySelectorAll('video');
            const showControls = this.dataset.showControls === 'true';

            videos.forEach(video => {
                video.addEventListener('mouseenter', () => {
                    if (showControls) video.setAttribute('controls', 'controls');
                    video.play().catch(() => { });
                });
                video.addEventListener('mouseleave', () => {
                    if (showControls) video.removeAttribute('controls');
                    video.pause();
                });
                video.addEventListener('click', () => {
                    if (video.paused) {
                        if (showControls) video.setAttribute('controls', 'controls');
                        video.play().catch(() => { });
                    } else {
                        if (!video.hasAttribute('controls')) {
                            video.pause();
                        }
                    }
                });
            });

            this.animate();
        }

        animate() {
            if (!this.isPaused) {
                this.position -= this.speed;

                // Track width is 1/3 of the total scroll width (since we cloned 3 times)
                const trackWidth = this.track.scrollWidth / 3;
                if (Math.abs(this.position) >= trackWidth) {
                    this.position = 0;
                }

                this.track.style.transform = `translateX(${this.position}px)`;
            }
            requestAnimationFrame(() => this.animate());
        }
    }
    customElements.define('scape-video-marquee', ScapeVideoMarquee);
}

/* ==========================================================================
   FLOATING VIDEO SECTION SCRIPT
   ========================================================================== */
document.addEventListener('DOMContentLoaded', function () {
    const floatingVideos = document.querySelectorAll('.scape-floating-video');

    floatingVideos.forEach(function (floatingVideo) {
        const inner = floatingVideo.querySelector('.scape-floating-video__inner');
        const minimizeBtn = floatingVideo.querySelector('.scape-floating-video__minimize');
        const closeBtn = floatingVideo.querySelector('.scape-floating-video__close');
        const videoEl = floatingVideo.querySelector('video');
        const iframeEl = floatingVideo.querySelector('iframe');

        if (inner) {
            inner.addEventListener('click', function (e) {
                if (e.target.closest('.scape-floating-video__close') || e.target.closest('.scape-floating-video__minimize')) return;

                if (floatingVideo.getAttribute('data-expanded') === 'false') {
                    floatingVideo.setAttribute('data-expanded', 'true');
                    if (videoEl) {
                        videoEl.muted = false;
                        videoEl.controls = true;
                        videoEl.currentTime = 0; // Restart from beginning when opened
                        videoEl.play();
                    } else if (iframeEl) {
                        iframeEl.style.pointerEvents = 'auto';
                    }
                }
            });
        }

        if (minimizeBtn) {
            minimizeBtn.addEventListener('click', function (e) {
                e.stopPropagation();
                floatingVideo.setAttribute('data-expanded', 'false');
                if (videoEl) {
                    videoEl.muted = true;
                    videoEl.controls = false;
                } else if (iframeEl) {
                    iframeEl.style.pointerEvents = 'none';
                }
            });
        }

        if (closeBtn) {
            closeBtn.addEventListener('click', function (e) {
                e.stopPropagation();
                floatingVideo.setAttribute('data-dismissed', 'true');
                if (videoEl) {
                    videoEl.pause();
                }
            });
        }
    });

    // Features Bar Mobile Slider
    if (!customElements.get('feature-slider')) {
        class FeatureSlider extends HTMLElement {
            connectedCallback() {
                this.track = this.querySelector('.scape-features__container');
                this.items = this.querySelectorAll('.scape-feature__item');
                if (!this.track || this.items.length < 2) return;

                this.autoplayInterval = null;
                this.speed = 3000;

                this.checkMobile = this.checkMobile.bind(this);
                this.checkMobile();
                window.addEventListener('resize', this.checkMobile);

                this.track.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
                this.track.addEventListener('touchend', () => {
                    setTimeout(() => this.checkMobile(), 3000);
                }, { passive: true });
            }

            disconnectedCallback() {
                window.removeEventListener('resize', this.checkMobile);
                this.stopAutoplay();
            }

            checkMobile() {
                const isMobile = window.innerWidth <= 768;
                if (isMobile) {
                    this.startAutoplay();
                } else {
                    this.stopAutoplay();
                }
            }

            startAutoplay() {
                if (this.autoplayInterval) return;
                this.autoplayInterval = setInterval(() => {
                    if (window.innerWidth > 768) {
                        this.stopAutoplay();
                        return;
                    }
                    const scrollLeft = this.track.scrollLeft;
                    const itemWidth = this.items[0].offsetWidth;
                    let currentIndex = Math.round(scrollLeft / itemWidth);

                    let nextIndex = currentIndex + 1;
                    if (nextIndex >= this.items.length) {
                        nextIndex = 0;
                    }

                    this.track.scrollTo({
                        left: nextIndex * itemWidth,
                        behavior: 'smooth'
                    });
                }, this.speed);
            }

            stopAutoplay() {
                if (this.autoplayInterval) {
                    clearInterval(this.autoplayInterval);
                    this.autoplayInterval = null;
                }
            }
        }
        customElements.define('feature-slider', FeatureSlider);
    }

    // Generic Mobile Autoplay Slider
    if (!customElements.get('mobile-autoplay-slider')) {
        class MobileAutoplaySlider extends HTMLElement {
            connectedCallback() {
                this.track = this.querySelector('[data-slider-track]');
                this.items = this.querySelectorAll('[data-slider-item]');
                if (!this.track || this.items.length < 2) return;

                this.autoplayInterval = null;
                this.speed = parseInt(this.dataset.speed) || 3000;
                this.breakpoint = parseInt(this.dataset.breakpoint) || 600;

                this.checkMobile = this.checkMobile.bind(this);
                this.checkMobile();
                window.addEventListener('resize', this.checkMobile);

                this.track.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
                const restartAutoplay = () => {
                    setTimeout(() => this.checkMobile(), 3000);
                };
                this.track.addEventListener('touchend', restartAutoplay, { passive: true });
                this.track.addEventListener('touchcancel', restartAutoplay, { passive: true });
            }

            disconnectedCallback() {
                window.removeEventListener('resize', this.checkMobile);
                this.stopAutoplay();
            }

            checkMobile() {
                const isMobile = window.innerWidth <= this.breakpoint;
                if (isMobile) {
                    this.startAutoplay();
                } else {
                    this.stopAutoplay();
                }
            }

            startAutoplay() {
                if (this.autoplayInterval) return;
                // Autoplay disabled per request for grid progress bar sliders
                /*
                this.autoplayInterval = setInterval(() => {
                    if (window.innerWidth > this.breakpoint) {
                        this.stopAutoplay();
                        return;
                    }
                    const isVertical = this.dataset.direction === 'vertical';
                    let activeIndex = 0;
                    let minDistance = Infinity;
                    const trackRect = this.track.getBoundingClientRect();
                    
                    // Skip if the slider is currently hidden (e.g. inside an inactive tab)
                    if (trackRect.width === 0 && trackRect.height === 0) return;

                    this.items.forEach((item, index) => {
                        const itemRect = item.getBoundingClientRect();
                        let distance;
                        if (isVertical) {
                            const trackCenter = trackRect.top + trackRect.height / 2;
                            const itemCenter = itemRect.top + itemRect.height / 2;
                            distance = Math.abs(trackCenter - itemCenter);
                        } else {
                            const trackCenter = trackRect.left + trackRect.width / 2;
                            const itemCenter = itemRect.left + itemRect.width / 2;
                            distance = Math.abs(trackCenter - itemCenter);
                        }
                        if (distance < minDistance) {
                            minDistance = distance;
                            activeIndex = index;
                        }
                    });

                    let nextIndex = activeIndex + 1;
                    
                    // Force loop to start if we've reached the very end of the scrollable track
                    const atEnd = isVertical 
                        ? Math.ceil(this.track.scrollTop + this.track.clientHeight) >= this.track.scrollHeight - 2
                        : Math.ceil(this.track.scrollLeft + this.track.clientWidth) >= this.track.scrollWidth - 2;
                        
                    if (atEnd || nextIndex >= this.items.length) {
                        nextIndex = 0;
                    }

                    const nextItem = this.items[nextIndex];
                    const nextItemRect = nextItem.getBoundingClientRect();

                    const performScroll = (el, left, top, isVert) => {
                        const originalSnap = el.style.scrollSnapType;
                        el.style.scrollSnapType = 'none';
                        try {
                            if (isVert) el.scrollTo({ top: top, behavior: 'smooth' });
                            else el.scrollTo({ left: left, behavior: 'smooth' });
                        } catch(e) {
                            if (isVert) el.scrollTop = top;
                            else el.scrollLeft = left;
                        }
                        setTimeout(() => {
                            el.style.scrollSnapType = originalSnap || '';
                        }, 600); // Re-enable after smooth scroll completes
                    };

                    if (isVertical) {
                        const scrollOffset = nextItemRect.top - trackRect.top + this.track.scrollTop - (trackRect.height - nextItemRect.height) / 2;
                        performScroll(this.track, 0, scrollOffset, true);
                    } else {
                        const scrollOffset = nextItemRect.left - trackRect.left + this.track.scrollLeft;
                        performScroll(this.track, scrollOffset, 0, false);
                    }
                }, this.speed);
                */
            }

            stopAutoplay() {
                if (this.autoplayInterval) {
                    clearInterval(this.autoplayInterval);
                    this.autoplayInterval = null;
                }
            }
        }
        customElements.define('mobile-autoplay-slider', MobileAutoplaySlider);
    }
});
